import StakeholderCombobox from "@app/components/stakeholder-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { useToast } from "@app/components/toast";
import { environment } from "@app/relay/relay-environment";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { commitMutation, graphql } from "react-relay";

import type { walletLedgerCells_AttributeMutation } from "@app/queries/walletLedgerCells_AttributeMutation.graphql";
import type { walletLedgerCells_AssignNatureMutation } from "@app/queries/walletLedgerCells_AssignNatureMutation.graphql";
import type { walletLedgerCells_CategorizeMutation } from "@app/queries/walletLedgerCells_CategorizeMutation.graphql";

// The three click-to-edit cells of the wallet ledger: counterparty, category and
// nature. Each is a label that opens a popover in place — the same edits the bulk
// toolbar offers, but reachable on a single row without selecting it first, plus
// the "Create rule…" escape hatch so a fix can be turned into a standing rule on
// the fly.
//
// The category cell is deliberately NOT a per-transaction edit: a category belongs
// to the counterparty, so picking one recategorizes the whole counterparty. When
// that counterparty is split across several categories, the pick is confirmed
// first — replacing the allocation would erase the split.

export type CategoryOption = {
  readonly id: string;
  readonly name: string;
  readonly parent: { readonly id: string } | null | undefined;
};

export type NatureOption = { readonly id: string; readonly name: string };

// Row shape these cells need on top of TxnTableRow. The wallet ledger query
// selects exactly this.
export type LedgerRow = {
  readonly txn: { readonly id: string };
  readonly description: string;
  readonly counterparty: { readonly id: string; readonly name: string } | null | undefined;
  readonly categoryNode: { readonly id: string } | null | undefined;
  readonly categorySliceCount: number;
  readonly expenseNature: { readonly id: string; readonly name: string } | null | undefined;
};

const attributeMutation = graphql`
  mutation walletLedgerCells_AttributeMutation($input: AttributeTxnsInputObject!) {
    attributeTxns(input: $input) {
      stakeholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

const assignNatureMutation = graphql`
  mutation walletLedgerCells_AssignNatureMutation($input: AssignTxnNatureInputObject!) {
    assignTxnNature(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const categorizeMutation = graphql`
  mutation walletLedgerCells_CategorizeMutation($input: CategorizeStakeholderInputObject!) {
    categorizeStakeholder(input: $input) {
      stakeholder {
        id
      }
      errors {
        message
      }
    }
  }
`;

// Root → … → leaf names for a category id. The tree is at most 3 deep
// (CreateCategoryMutation caps it), so the walk is bounded; the depth guard just
// makes a cyclic parent chain impossible to hang on.
export function categoryPath(
  categoryId: string | null | undefined,
  byId: ReadonlyMap<string, CategoryOption>
): string[] {
  const path: string[] = [];
  let current = categoryId ? byId.get(categoryId) : undefined;
  while (current && path.length < 8) {
    path.unshift(current.name);
    current = current.parent ? byId.get(current.parent.id) : undefined;
  }
  return path;
}

// Where to put the panel for a trigger at `rect`: below it by default, flipped
// above when the viewport floor is closer, and pulled left so a right-hand column
// never opens off-screen. Height is capped to the space it actually has.
function panelPosition(rect: DOMRect, width: number): CSSProperties {
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  const below = window.innerHeight - rect.bottom;
  const flip = below < 260 && rect.top > below;
  return flip
    ? { position: "fixed", left, width, bottom: window.innerHeight - rect.top + 4, maxHeight: rect.top - 16 }
    : { position: "fixed", left, width, top: rect.bottom + 4, maxHeight: below - 16 };
}

// A cell that turns into a popover on click. The panel is position:FIXED and
// placed off the trigger's box, so the ledger's scrolling table body cannot clip
// it on the lower rows — but it stays in the dialog's own DOM subtree rather than
// being portalled to <body>, because Radix's modal Dialog sets
// `pointer-events: none` on the body and anything portalled there would render
// but never accept a click. (The dialog is deliberately centred with inset/margin
// rather than a transform: a transformed ancestor would become the containing
// block for these fixed panels and clip them again.)
// Closes on outside-click, Escape, a scroll (the anchor would drift), or a pick.
function CellPopover({
  title,
  children,
  width = 288,
  onClose,
}: {
  title: ReactNode;
  children: (close: () => void) => ReactNode;
  width?: number;
  onClose?: () => void;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const close = () => {
    setRect(null);
    onClose?.();
  };

  useEffect(() => {
    if (!rect) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      close();
    };
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && close();
    // The table body scrolling under the panel would strand it at a stale anchor,
    // so that closes it — but the panel's OWN scrollable list (the category and
    // nature pickers) must not, or the list would be unscrollable.
    const onScroll = (event: Event) => {
      if (panelRef.current?.contains(event.target as Node)) return;
      close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [rect]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() =>
          rect ? close() : setRect(triggerRef.current?.getBoundingClientRect() ?? null)
        }
        className="-mx-1 block max-w-full rounded px-1 py-0.5 text-left hover:bg-gray-100 hover:ring-1 hover:ring-gray-200"
      >
        {title}
      </button>
      {rect && (
        <div
          ref={panelRef}
          style={panelPosition(rect, width)}
          className="z-[60] overflow-y-auto rounded-lg border bg-white p-2 text-gray-700 shadow-xl"
        >
          {children(close)}
        </div>
      )}
    </>
  );
}

const createRuleItem = (onCreateRule: () => void, close: () => void) => (
  <button
    type="button"
    onClick={() => {
      close();
      onCreateRule();
    }}
    className="mt-1 block w-full rounded border-t px-2 py-1.5 text-left text-sm font-medium text-gray-900 hover:bg-gray-100"
  >
    Create rule…
  </button>
);

export function CounterpartyCell({
  row,
  stakeholders,
  perspectiveId,
  busy,
  setBusy,
  onChanged,
  onCreateRule,
}: {
  row: LedgerRow;
  stakeholders: ReadonlyArray<StakeholderOption>;
  perspectiveId: string | null;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onChanged: () => void;
  onCreateRule: () => void;
}) {
  const notify = useToast();

  const attribute = (choice: { stakeholderId?: string; name?: string }) => {
    setBusy(true);
    commitMutation<walletLedgerCells_AttributeMutation>(environment, {
      mutation: attributeMutation,
      variables: {
        input: {
          txnIds: [row.txn.id],
          stakeholderId: choice.stakeholderId,
          name: choice.name,
          perspectiveId: perspectiveId ?? undefined,
        },
      },
      onCompleted: (response) => {
        setBusy(false);
        const failure = response.attributeTxns?.errors?.[0]?.message;
        if (failure) return notify({ message: failure, kind: "error" });
        notify({ message: "Counterparty updated.", kind: "success" });
        onChanged();
      },
      onError: () => {
        setBusy(false);
        notify({ message: "Could not set the counterparty — please try again.", kind: "error" });
      },
    });
  };

  return (
    <CellPopover
      title={
        <span className={row.counterparty ? "" : "text-gray-400"}>
          {row.counterparty?.name ?? "Set counterparty…"}
        </span>
      }
    >
      {(close) => (
        <>
          <StakeholderCombobox
            options={stakeholders}
            onPick={(choice) => {
              close();
              if (!busy) attribute(choice);
            }}
          />
          {createRuleItem(onCreateRule, close)}
        </>
      )}
    </CellPopover>
  );
}

export function CategoryCell({
  row,
  categories,
  categoriesById,
  perspectiveId,
  busy,
  setBusy,
  onChanged,
}: {
  row: LedgerRow;
  categories: ReadonlyArray<CategoryOption>;
  categoriesById: ReadonlyMap<string, CategoryOption>;
  perspectiveId: string | null;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onChanged: () => void;
}) {
  const notify = useToast();
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState<CategoryOption | null>(null);
  const counterparty = row.counterparty;

  const path = categoryPath(row.categoryNode?.id, categoriesById);

  // Every category as its full breadcrumb, so the picker disambiguates the
  // "Software" that lives under R&D from the one under G&A.
  const options = useMemo(
    () =>
      categories
        .map((category) => ({ category, path: categoryPath(category.id, categoriesById) }))
        .sort((a, b) => a.path.join(" ").localeCompare(b.path.join(" ")))
        .filter(({ path: crumbs }) =>
          crumbs.join(" ").toLowerCase().includes(query.trim().toLowerCase())
        )
        .slice(0, 40),
    [categories, categoriesById, query]
  );

  const categorize = (category: CategoryOption, close: () => void) => {
    if (!counterparty || !perspectiveId) return;
    setBusy(true);
    commitMutation<walletLedgerCells_CategorizeMutation>(environment, {
      mutation: categorizeMutation,
      variables: {
        input: {
          stakeholderId: counterparty.id,
          perspectiveId,
          allocations: [{ categoryId: category.id, sliceType: "PERCENTAGE", weight: 1, ordinal: 0 }],
        },
      },
      onCompleted: (response) => {
        setBusy(false);
        setPending(null);
        close();
        const failure = response.categorizeStakeholder?.errors?.[0]?.message;
        if (failure) return notify({ message: failure, kind: "error" });
        notify({
          message: `${counterparty.name} is now categorized as ${category.name}.`,
          kind: "success",
        });
        onChanged();
      },
      onError: () => {
        setBusy(false);
        notify({ message: "Could not recategorize — please try again.", kind: "error" });
      },
    });
  };

  return (
    <CellPopover
      width={384}
      onClose={() => {
        setPending(null);
        setQuery("");
      }}
      title={
        path.length > 0 ? (
          <span className="whitespace-normal">
            {path.map((crumb, index) => (
              <span key={`${crumb}-${index}`}>
                {index > 0 && <span className="text-gray-300"> › </span>}
                <span className={index === path.length - 1 ? "font-medium" : "text-emerald-600/70"}>
                  {crumb}
                </span>
              </span>
            ))}
          </span>
        ) : (
          <span className="text-gray-400">Uncategorized</span>
        )
      }
    >
      {(close) =>
        !counterparty ? (
          <p className="px-1 py-1.5 text-xs text-gray-500">
            Give this transaction a counterparty first — a category belongs to the
            counterparty, not the transaction.
          </p>
        ) : pending ? (
          <div className="space-y-2 text-sm">
            <p className="font-medium text-gray-900">Replace a split allocation?</p>
            <p className="text-xs text-gray-600">
              {counterparty.name} is currently split across {row.categorySliceCount}{" "}
              categories. Moving it to{" "}
              <span className="font-medium">{categoryPath(pending.id, categoriesById).join(" › ")}</span>{" "}
              replaces the whole split with a single 100% allocation.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPending(null)}
                className="rounded border px-2.5 py-1 text-xs hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => categorize(pending, close)}
                className="rounded bg-gray-900 px-2.5 py-1 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                Replace split
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm">
            <p className="px-1 pb-1.5 text-xs text-gray-500">
              Recategorizes <span className="font-medium">{counterparty.name}</span> everywhere,
              not just this transaction.
            </p>
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Find a category…"
              className="w-full rounded border px-2 py-1.5 text-sm"
            />
            <div className="mt-1 max-h-72 overflow-y-auto">
              {options.length === 0 && (
                <p className="px-2 py-1.5 text-xs text-gray-400">No category matches.</p>
              )}
              {options.map(({ category, path: crumbs }) => (
                <button
                  key={category.id}
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    row.categorySliceCount > 1 ? setPending(category) : categorize(category, close)
                  }
                  className="block w-full rounded px-2 py-1.5 text-left hover:bg-gray-50 disabled:opacity-50"
                >
                  {crumbs.slice(0, -1).map((crumb) => (
                    <span key={crumb} className="text-gray-400">
                      {crumb} ›{" "}
                    </span>
                  ))}
                  <span className="font-medium text-gray-900">{crumbs[crumbs.length - 1]}</span>
                </button>
              ))}
            </div>
          </div>
        )
      }
    </CellPopover>
  );
}

export function NatureCell({
  row,
  natures,
  busy,
  setBusy,
  onChanged,
  onCreateRule,
}: {
  row: LedgerRow;
  natures: ReadonlyArray<NatureOption>;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onChanged: () => void;
  onCreateRule: () => void;
}) {
  const notify = useToast();

  const tag = (expenseNatureId: string | null, close: () => void) => {
    setBusy(true);
    commitMutation<walletLedgerCells_AssignNatureMutation>(environment, {
      mutation: assignNatureMutation,
      variables: { input: { txnIds: [row.txn.id], expenseNatureId } },
      onCompleted: (response) => {
        setBusy(false);
        close();
        const failure = response.assignTxnNature?.errors?.[0]?.message;
        if (failure) return notify({ message: failure, kind: "error" });
        notify({ message: expenseNatureId ? "Nature tagged." : "Nature cleared.", kind: "success" });
        onChanged();
      },
      onError: () => {
        setBusy(false);
        notify({ message: "Could not tag the nature — please try again.", kind: "error" });
      },
    });
  };

  return (
    <CellPopover
      title={
        <span className={row.expenseNature ? "" : "text-gray-400"}>
          {row.expenseNature?.name ?? "Set nature…"}
        </span>
      }
    >
      {(close) => (
        <div className="text-sm">
          <div className="max-h-72 overflow-y-auto">
            <button
              type="button"
              disabled={busy}
              onClick={() => tag(null, close)}
              className="block w-full rounded px-2 py-1.5 text-left text-gray-500 hover:bg-gray-50 disabled:opacity-50"
            >
              Clear nature
            </button>
            {natures.map((nature) => (
              <button
                key={nature.id}
                type="button"
                disabled={busy}
                onClick={() => tag(nature.id, close)}
                className="block w-full rounded px-2 py-1.5 text-left hover:bg-gray-50 disabled:opacity-50"
              >
                {nature.name}
              </button>
            ))}
          </div>
          {createRuleItem(onCreateRule, close)}
        </div>
      )}
    </CellPopover>
  );
}
